import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Heart, Calendar, MapPin, CheckCircle, Users, Youtube, Music } from '../icons';
import { getWeddingWebsite, submitPublicRsvp } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import mediaUrl from '../utils/mediaUrl';

// Same matcher the editor (WeddingWebsite.js) uses for its preview
const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
};

function PublicWedding() {
  const { subdomain } = useParams();
  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rsvpForm, setRsvpForm] = useState({ name: '', email: '', rsvp_status: '', meal_preference: 'Non-vegetarian', adult_count: 1, kids_count: 0 });
  const [rsvpMessage, setRsvpMessage] = useState(null);
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const audioRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    getWeddingWebsite(subdomain)
      .then((res) => setWebsite(res.data))
      // A 404 means missing or unpublished; anything else is worth a retry
      .catch((err) => setError(err.response?.status === 404 ? 'publicWedding.notFound' : 'publicWedding.loadError'))
      .finally(() => setLoading(false));
  }, [subdomain]);

  if (loading) return <div className="loading-page"><div className="spinner"></div></div>;

  if (error || !website) {
    return (
      <div className="wedding-website-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Heart size={48} style={{ color: '#be185d', marginBottom: '16px' }} />
          <h2>{t('publicWedding.title')}</h2>
          <p style={{ color: '#6b7280', marginTop: '8px' }}>{t(error || 'publicWedding.pageNotFound')}</p>
          <Link to="/" className="btn btn-primary" style={{ marginTop: '16px', display: 'inline-block' }}>{t('publicWedding.goHome')}</Link>
        </div>
      </div>
    );
  }

  const familyMembers = Array.isArray(website.family_members) ? website.family_members : [];
  const youtubeEmbed = getYoutubeEmbedUrl(website.youtube_url);

  const weddingDate = website.wedding_date ? new Date(website.wedding_date) : null;
  const formattedDate = weddingDate
    ? weddingDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : t('publicWedding.dateTBD');

  const events = Array.isArray(website.event_details) ? website.event_details : [];
  const storyText = website.our_story || website.story || '';

  const daysUntil = weddingDate ? Math.ceil((weddingDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const months = daysUntil ? Math.floor(daysUntil / 30) : 0;
  const remainingDays = daysUntil ? daysUntil % 30 : 0;

  const handleRsvp = async (e) => {
    e.preventDefault();
    if (!rsvpForm.name || !rsvpForm.email || !rsvpForm.rsvp_status) return;
    setRsvpSubmitting(true);
    setRsvpMessage(null);
    try {
      const nameParts = rsvpForm.name.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ');
      const res = await submitPublicRsvp({
        wedding_id: website.wedding_id,
        first_name: firstName,
        last_name: lastName,
        email: rsvpForm.email,
        rsvp_status: rsvpForm.rsvp_status,
        meal_preference: rsvpForm.meal_preference,
        adult_count: rsvpForm.rsvp_status === 'accepted' ? rsvpForm.adult_count : 0,
        kids_count: rsvpForm.rsvp_status === 'accepted' ? rsvpForm.kids_count : 0
      });
      const successText = res.data?.isUpdate
        ? (t('publicWedding.rsvpUpdated') || 'Your RSVP has been updated!')
        : t('publicWedding.rsvpSuccess');
      setRsvpMessage({ type: 'success', text: successText });
      setRsvpForm({ name: '', email: '', rsvp_status: '', meal_preference: 'Non-vegetarian', adult_count: 1, kids_count: 0 });
    } catch (err) {
      setRsvpMessage({ type: 'error', text: err.response?.data?.error || t('publicWedding.rsvpError') });
    } finally {
      setRsvpSubmitting(false);
    }
  };

  return (
    <div className="wedding-website-page">
      {/* Hero Section */}
      <section className="ww-hero" style={website.background_image_url ? { backgroundImage: `url(${website.background_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
        <div className="ww-hero-overlay"></div>
        <div className="ww-hero-content">
          <div className="ww-names">
            <span className="ww-name">{website.partner1_name || t('publicWedding.partner1')}</span>
            <Heart className="ww-heart" size={32} />
            <span className="ww-name">{website.partner2_name || t('publicWedding.partner2')}</span>
          </div>
          <div className="ww-date">
            <Calendar size={18} />
            <span>{formattedDate}</span>
          </div>
          <div className="ww-location">
            <MapPin size={18} />
            <span>{website.venue_location || t('publicWedding.venueTBD')}, {website.city || 'Nepal'}</span>
          </div>
        </div>
        <div className="ww-scroll-hint">{t('publicWedding.scrollStory')} ↓</div>
      </section>

      {/* Our Story */}
      {storyText && (
        <section className="ww-section ww-story">
          <div className="ww-container">
            <h2 className="ww-section-title">{t('publicWedding.ourStory')}</h2>
            <div className="ww-divider"><Heart size={16} /></div>
            <p className="ww-story-text">{storyText}</p>
          </div>
        </section>
      )}

      {/* Family, video and music are edited on /wedding-website; until now only
          the couple's own preview showed them. Markup mirrors that page. */}
      {familyMembers.length > 0 && (
        <section className="ww-section" style={{ background: '#fefce8' }}>
          <div className="ww-container">
            <h2 className="ww-section-title"><Users size={24} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} />{t('weddingWebsite.ourFamily')}</h2>
            <div className="ww-divider"><Heart size={16} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '20px', maxWidth: '800px', margin: '0 auto' }}>
              {familyMembers.map((member) => (
                <div key={member.id} style={{ textAlign: 'center' }}>
                  <div style={{ width: '100px', height: '100px', borderRadius: '50%', margin: '0 auto 8px', overflow: 'hidden', border: '3px solid #fbbf24', background: '#f3f4f6' }}>
                    {member.photo_url ? (
                      <img src={mediaUrl(member.photo_url)} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', color: '#9ca3af' }}>
                        {(member.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 2px', color: '#1f2937' }}>{member.name}</h4>
                  <span style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>{member.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Events */}
      {events.length > 0 && (
        <section className="ww-section ww-events">
          <div className="ww-container">
            <h2 className="ww-section-title">{t('publicWedding.weddingEvents')}</h2>
            <div className="ww-divider"><Heart size={16} /></div>
            <div className="ww-events-list">
              {events.map((event, idx) => (
                <div key={idx} className="ww-event-card">
                  <div className="ww-event-icon">
                    {idx === 0 ? '✋' : idx === 1 ? '💒' : '🎉'}
                  </div>
                  <h3>{event.name}</h3>
                  <div className="ww-event-details">
                    <span><Calendar size={14} /> {event.date ? new Date(event.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : t('publicWedding.dateTBD')} {event.time ? `at ${event.time}` : ''}</span>
                    <span><MapPin size={14} /> {event.venue || t('publicWedding.venueTBD')}</span>
                  </div>
                  <p>{event.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Countdown */}
      {daysUntil && daysUntil > 0 && (
        <section className="ww-section ww-countdown">
          <div className="ww-container">
            <h2 className="ww-section-title">{t('publicWedding.countingDown')}</h2>
            <div className="ww-countdown-grid">
              <div className="countdown-unit">
                <span className="countdown-num">{months}</span>
                <span className="countdown-lbl">{t('publicWedding.months')}</span>
              </div>
              <div className="countdown-unit">
                <span className="countdown-num">{remainingDays}</span>
                <span className="countdown-lbl">{t('publicWedding.days')}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* YouTube / Live Stream */}
      {youtubeEmbed && (
        <section className="ww-section" style={{ background: '#fdf2f8' }}>
          <div className="ww-container">
            <h2 className="ww-section-title"><Youtube size={24} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} />{t('weddingWebsite.liveStream')}</h2>
            <div className="ww-divider"><Heart size={16} /></div>
            <div style={{ maxWidth: '720px', margin: '0 auto', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                <iframe src={youtubeEmbed} title="Wedding Video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Background Music */}
      {website.music_url && (
        <section className="ww-section" style={{ background: '#f0fdf4' }}>
          <div className="ww-container">
            <h2 className="ww-section-title"><Music size={24} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} />{t('weddingWebsite.backgroundMusic')}</h2>
            <div className="ww-divider"><Heart size={16} /></div>
            <div style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
              <audio ref={audioRef} src={mediaUrl(website.music_url)} loop onEnded={() => setMusicPlaying(false)} />
              <button className="btn btn-primary" onClick={() => {
                if (!audioRef.current) return;
                if (musicPlaying) { audioRef.current.pause(); setMusicPlaying(false); }
                // play() rejects on a bad URL; leave the button on "Play" then
                else { audioRef.current.play().then(() => setMusicPlaying(true)).catch(() => setMusicPlaying(false)); }
              }} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 28px', borderRadius: '50px' }}>
                <Music size={18} /> {musicPlaying ? '⏸ ' + t('weddingWebsite.pauseMusic') : '▶ ' + t('weddingWebsite.playMusic')}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* RSVP */}
      <section className="ww-section ww-rsvp">
        <div className="ww-container">
          <h2 className="ww-section-title">{t('publicWedding.rsvp')}</h2>
          <div className="ww-divider"><Heart size={16} /></div>
          <p>{t('publicWedding.rsvpHonored')}</p>
          {rsvpMessage && (
            <div style={{
              padding: '16px 20px', borderRadius: '12px', marginBottom: '20px', maxWidth: '500px', margin: '0 auto 20px',
              background: rsvpMessage.type === 'success' ? '#f0fdf4' : '#fef2f2',
              color: rsvpMessage.type === 'success' ? '#166534' : '#991b1b',
              border: `1px solid ${rsvpMessage.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
              display: 'flex', alignItems: 'center', gap: '10px'
            }}>
              {rsvpMessage.type === 'success' && <CheckCircle size={20} />}
              {rsvpMessage.text}
            </div>
          )}
          <form className="ww-rsvp-form" onSubmit={handleRsvp}>
            <input type="text" placeholder={t('publicWedding.yourFullName')} required value={rsvpForm.name} onChange={(e) => setRsvpForm({ ...rsvpForm, name: e.target.value })} />
            <input type="email" placeholder={t('publicWedding.emailAddress')} required value={rsvpForm.email} onChange={(e) => setRsvpForm({ ...rsvpForm, email: e.target.value })} />
            <select required value={rsvpForm.rsvp_status} onChange={(e) => setRsvpForm({ ...rsvpForm, rsvp_status: e.target.value })}>
              <option value="">{t('publicWedding.willYouAttend')}</option>
              <option value="accepted">{t('publicWedding.joyfullyAccept')} 🎉</option>
              <option value="declined">{t('publicWedding.regretfullyDecline')} 😢</option>
              <option value="maybe">{t('publicWedding.notSureYet')} 🤔</option>
            </select>
            <select value={rsvpForm.meal_preference} onChange={(e) => setRsvpForm({ ...rsvpForm, meal_preference: e.target.value })}>
              <option value="Non-vegetarian">{t('publicWedding.nonVeg')}</option>
              <option value="Vegetarian">{t('publicWedding.vegetarian')}</option>
              <option value="Vegan">{t('publicWedding.vegan')}</option>
            </select>
            {rsvpForm.rsvp_status === 'accepted' && (
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#374151' }}>{t('publicWedding.adults') || 'Adults'}</label>
                  <input type="number" min="1" max="20" value={rsvpForm.adult_count} onChange={(e) => setRsvpForm({ ...rsvpForm, adult_count: parseInt(e.target.value) || 1 })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#374151' }}>{t('publicWedding.kids') || 'Kids'}</label>
                  <input type="number" min="0" max="20" value={rsvpForm.kids_count} onChange={(e) => setRsvpForm({ ...rsvpForm, kids_count: parseInt(e.target.value) || 0 })} />
                </div>
              </div>
            )}
            <button type="submit" className="btn btn-primary btn-lg" disabled={rsvpSubmitting}>
              {rsvpSubmitting ? t('common.sending') : t('publicWedding.sendRsvp')}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <div className="ww-footer">
        <Heart size={20} />
        <p>{website.partner1_name} & {website.partner2_name}</p>
        <span>{formattedDate} • {website.city || 'Nepal'}</span>
        <small>{t('publicWedding.poweredBy')}</small>
      </div>
    </div>
  );
}

export default PublicWedding;
